import { useState } from "react";
import { Link } from "react-router-dom";

import { HubUserAvatar } from "@/components/HubUserAvatar";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { CardTitleWithHelp, HoverHintTrigger } from "@/components/ui/title-help";
import { useIntegratorMe } from "@/lib/me-query";
import { cn, EM_DASH } from "@/lib/utils";

import { investigationStatusLabel, type InvestigationStatus } from "./casesApi";
import type { NcrDeviceWork } from "./qualityRcaApi";
import { QualityRcaDevicesCard, type RcaWorksheetDevice } from "./QualityRcaDevicesCard";
import { rmaStatusLabel } from "./rmaApi";
import { RCA_DISPOSITION_FLAG, RCA_FLOW_STEPS, type RcaFlowStep, type RcaFormState, type RcaReport, type RcaStepRow } from "./rcaForm";

type Sheet = "information" | "draft" | "final";

export interface RcaDeviceAutomaticLog {
  receivedAt: string | null;
  loggedAt: string | null;
  loggedBy: string | null;
}

export interface RcaLinkedInvestigation {
  investigationId: number;
  qualityCaseId: number;
  investigationStatus: string;
  caseTitle: string;
}

export interface RcaLinkedRma {
  id: number;
  rmaNumber: string;
  status: string;
  customerName: string | null;
  contactName: string | null;
  contactPhone: string | null;
  dateReceived: string | null;
  quantityAndModels: string | null;
  complaint: string | null;
}

export interface RcaLinkedIncident {
  id: number;
  serials: string[];
  skus: string[];
  open: boolean;
  repairScrap: string | null;
}

export interface RcaLinkedDocuments {
  investigations: RcaLinkedInvestigation[];
  rmas: RcaLinkedRma[];
  incidents: RcaLinkedIncident[];
}

type LinkedDocumentTab = "investigation" | "rma" | "incident";

const inputClass =
  "w-full rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm text-slate-900";

const RCA_SHEETS: Array<{ id: Sheet; label: string }> = [
  { id: "information", label: "Information gained" },
  { id: "draft", label: "Draft report" },
  { id: "final", label: "Final report" },
];

export interface RcaWorksheetProps {
  form: RcaFormState;
  documents: RcaLinkedDocuments;
  devices: RcaWorksheetDevice[];
  deviceLogs: Record<string, RcaDeviceAutomaticLog>;
  pcbaLocationBySerial: Record<string, number>;
  ncrDeviceWork: Record<number, NcrDeviceWork>;
  rcaId: number;
  writesAllowed: boolean;
  onChange: (next: RcaFormState) => void;
  onSave: (next: RcaFormState) => void;
  onNcrChanged: () => Promise<void> | void;
}

export function QualityRcaWorksheet(props: RcaWorksheetProps) {
  const { form, onChange, onSave } = props;
  const [sheet, setSheet] = useState<Sheet>("information");

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-1 border-b border-slate-200 pb-px" role="tablist" aria-label="RCA sections">
        {RCA_SHEETS.map((tab) => {
          const selected = sheet === tab.id;
          const button = (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={selected}
              className={cn(
                "rounded-t-md px-3 py-2 text-sm font-medium transition-colors",
                selected
                  ? "relative z-10 border border-b-0 border-slate-200 bg-white text-slate-900 shadow-sm"
                  : "border border-transparent bg-slate-50 text-slate-600 hover:bg-slate-100",
              )}
              onClick={() => setSheet(tab.id)}
            >
              {tab.label}
            </button>
          );
          if (tab.id !== "information") return button;
          return (
            <HoverHintTrigger key={tab.id} trigger={button}>
              Internal working document. Capture everything: raw observations and incomplete notes stay with the team.
            </HoverHintTrigger>
          );
        })}
      </div>
      {sheet === "information" ? (
        <InformationSheet {...props} />
      ) : (
        <ReportSheet
          draft={sheet === "draft"}
          report={sheet === "draft" ? form.draft : form.final}
          onChange={(report) => onChange(sheet === "draft" ? { ...form, draft: report } : { ...form, final: report })}
          onSave={(report) => onSave(sheet === "draft" ? { ...form, draft: report } : { ...form, final: report })}
        />
      )}
    </div>
  );
}

const LINKED_DOCUMENT_TABS: Array<{ id: LinkedDocumentTab; label: string }> = [
  { id: "investigation", label: "Quality Investigation" },
  { id: "rma", label: "RMA" },
  { id: "incident", label: "NCR" },
];

function linkedDocumentCount(documents: RcaLinkedDocuments, tab: LinkedDocumentTab): number {
  if (tab === "investigation") return documents.investigations.length;
  if (tab === "rma") return documents.rmas.length;
  return documents.incidents.length;
}

function documentCell(value: string | null | undefined): string {
  const text = value?.trim() ?? "";
  return text === "" ? EM_DASH : text;
}

function rmaStatusText(status: string): string {
  if (status === "awaiting_return" || status === "received" || status === "closed" || status === "cancelled") {
    return rmaStatusLabel(status);
  }
  return status.trim() === "" ? EM_DASH : status;
}

function LinkedDocumentsCard({ documents }: { documents: RcaLinkedDocuments }) {
  const [tab, setTab] = useState<LinkedDocumentTab>("rma");
  return (
    <Card>
      <CardHeader>
        <CardTitle>Linked documents</CardTitle>
      </CardHeader>
      <CardBody className="space-y-4">
        <div className="flex flex-wrap gap-1 border-b border-slate-200 pb-px" role="tablist" aria-label="Linked documents">
          {LINKED_DOCUMENT_TABS.map((item) => {
            const selected = tab === item.id;
            const count = linkedDocumentCount(documents, item.id);
            return (
              <button
                key={item.id}
                type="button"
                role="tab"
                aria-selected={selected}
                className={cn(
                  "inline-flex items-center gap-2 rounded-t-md px-3 py-2 text-sm font-medium transition-colors",
                  selected
                    ? "relative z-10 border border-b-0 border-slate-200 bg-white text-slate-900 shadow-sm"
                    : "border border-transparent bg-slate-50 text-slate-600 hover:bg-slate-100",
                )}
                onClick={() => setTab(item.id)}
              >
                {item.label}
                <span
                  className={cn(
                    "rounded-full px-1.5 py-0.5 text-xs tabular-nums",
                    selected ? "bg-slate-100 text-slate-800" : "bg-slate-200 text-slate-600",
                  )}
                >
                  {count}
                </span>
              </button>
            );
          })}
        </div>
        {tab === "investigation" ? <InvestigationTable rows={documents.investigations} /> : null}
        {tab === "rma" ? <RmaDocumentTable rows={documents.rmas} /> : null}
        {tab === "incident" ? <IncidentTable rows={documents.incidents} /> : null}
      </CardBody>
    </Card>
  );
}

function InvestigationTable({ rows }: { rows: RcaLinkedInvestigation[] }) {
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-left text-sm">
        <thead className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
          <tr>
            <th className="px-3 py-2 font-medium">Investigation</th>
            <th className="px-3 py-2 font-medium">Quality case</th>
            <th className="px-3 py-2 font-medium">Status</th>
            <th className="px-3 py-2 font-medium">Title</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td className="px-3 py-2 text-slate-600" colSpan={4}>
                No quality investigations linked.
              </td>
            </tr>
          ) : (
            rows.map((row) => (
              <tr key={row.investigationId} className="border-b border-slate-100">
                <td className="px-3 py-2">
                  <Link
                    className="text-sky-800 hover:underline"
                    to={`/quality/cases/${row.qualityCaseId}/investigations/${row.investigationId}/traveler`}
                  >
                    Investigation {row.investigationId}
                  </Link>
                </td>
                <td className="px-3 py-2">
                  <Link className="text-sky-800 hover:underline" to={`/quality/cases/${row.qualityCaseId}`}>
                    Quality case {row.qualityCaseId}
                  </Link>
                </td>
                <td className="px-3 py-2">{investigationStatusLabel(row.investigationStatus as InvestigationStatus)}</td>
                <td className="px-3 py-2">{documentCell(row.caseTitle)}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

function RmaDocumentTable({ rows }: { rows: RcaLinkedRma[] }) {
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-left text-sm">
        <thead className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
          <tr>
            <th className="px-3 py-2 font-medium">RMA</th>
            <th className="px-3 py-2 font-medium">Status</th>
            <th className="px-3 py-2 font-medium">Customer</th>
            <th className="px-3 py-2 font-medium">Contact</th>
            <th className="px-3 py-2 font-medium">Phone</th>
            <th className="px-3 py-2 font-medium">Date received</th>
            <th className="px-3 py-2 font-medium">Model</th>
            <th className="px-3 py-2 font-medium">Reported issues</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td className="px-3 py-2 text-slate-600" colSpan={8}>
                No RMAs linked.
              </td>
            </tr>
          ) : (
            rows.map((row) => (
              <tr key={row.id} className="border-b border-slate-100">
                <td className="px-3 py-2">
                  <Link className="text-sky-800 hover:underline" to={`/quality/rmas/${row.id}`}>
                    {row.rmaNumber}
                  </Link>
                </td>
                <td className="px-3 py-2">{rmaStatusText(row.status)}</td>
                <td className="px-3 py-2">{documentCell(row.customerName)}</td>
                <td className="px-3 py-2">{documentCell(row.contactName)}</td>
                <td className="px-3 py-2">{documentCell(row.contactPhone)}</td>
                <td className="px-3 py-2">{documentCell(row.dateReceived)}</td>
                <td className="px-3 py-2">{documentCell(row.quantityAndModels)}</td>
                <td className="px-3 py-2">{documentCell(row.complaint)}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

function IncidentTable({ rows }: { rows: RcaLinkedIncident[] }) {
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-left text-sm">
        <thead className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
          <tr>
            <th className="px-3 py-2 font-medium">NCR</th>
            <th className="px-3 py-2 font-medium">Serial</th>
            <th className="px-3 py-2 font-medium">SKU</th>
            <th className="px-3 py-2 font-medium">Status</th>
            <th className="px-3 py-2 font-medium">Repair or scrap</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td className="px-3 py-2 text-slate-600" colSpan={5}>
                No NCRs linked.
              </td>
            </tr>
          ) : (
            rows.map((row) => (
              <tr key={row.id} className="border-b border-slate-100">
                <td className="px-3 py-2">
                  <Link className="text-sky-800 hover:underline" to={`/quality/ncrs/${row.id}`}>
                    NCR {row.id}
                  </Link>
                </td>
                <td className="px-3 py-2">{row.serials.length > 0 ? row.serials.join(", ") : EM_DASH}</td>
                <td className="px-3 py-2">{row.skus.length > 0 ? row.skus.join(", ") : EM_DASH}</td>
                <td className="px-3 py-2">{row.open ? "Open" : "Closed"}</td>
                <td className="px-3 py-2">{documentCell(row.repairScrap)}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

function InformationSheet({
  form,
  documents,
  devices,
  deviceLogs,
  pcbaLocationBySerial,
  ncrDeviceWork,
  rcaId,
  writesAllowed,
  onChange,
  onSave,
  onNcrChanged,
}: RcaWorksheetProps) {
  const meQuery = useIntegratorMe();
  const authorName = meQuery.data?.user.displayName?.trim() || meQuery.data?.user.email?.trim() || "";
  const [editorialDraft, setEditorialDraft] = useState("");
  const postedEditorials = form.editorials.filter((row) => row.notes.trim() !== "" || row.name.trim() !== "");
  const setConclusion = (key: "rootCause" | "evidence" | "openQuestions" | "correctiveActions", value: string) => {
    onChange({ ...form, conclusion: { ...form.conclusion, [key]: value } });
  };
  const saveConclusion = (key: "rootCause" | "evidence" | "openQuestions" | "correctiveActions", value: string) => {
    onSave({ ...form, conclusion: { ...form.conclusion, [key]: value } });
  };
  const signOff = () => {
    const next = {
      ...form,
      conclusion: {
        ...form.conclusion,
        signOff: {
          name: authorName,
          date: new Date().toLocaleDateString("en-CA"),
          avatarUrl: meQuery.data?.user.avatarUrl?.trim() || "",
        },
      },
    };
    onChange(next);
    onSave(next);
  };

  return (
    <div className="space-y-4">
      <LinkedDocumentsCard documents={documents} />
      <QualityRcaDevicesCard
        devices={devices}
        deviceLogs={deviceLogs}
        pcbaLocationBySerial={pcbaLocationBySerial}
        ncrDeviceWork={ncrDeviceWork}
        form={form}
        rcaId={rcaId}
        writesAllowed={writesAllowed}
        onNcrChanged={onNcrChanged}
      />
      <RcaStatusFlow
        form={form}
        onChange={onChange}
        onSave={onSave}
      />
      <Card>
        <CardHeader>
          <CardTitle>Next-level troubleshooting</CardTitle>
        </CardHeader>
        <CardBody className="space-y-3">
          <p className="text-sm text-slate-600">
            Document follow-up steps from the initial intake. Example: replace a bad AA battery, confirm with batt, then capture stat and batt again.
          </p>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-slate-500">
                  <th className="px-2 py-1 font-medium">Subsection</th>
                  <th className="px-2 py-1 font-medium">Action taken</th>
                  <th className="px-2 py-1 font-medium">Result / observations</th>
                </tr>
              </thead>
              <tbody>
                {form.troubleshooting.map((row, index) => (
                  <tr key={`step-${index}`}>
                    <td className="px-2 py-1 align-top">
                      <input className={inputClass} value={row.subsection} onChange={(event) => updateStep(form, index, { subsection: event.target.value }, onChange)} onBlur={(event) => updateStep(form, index, { subsection: event.target.value }, onSave)} />
                    </td>
                    <td className="px-2 py-1 align-top">
                      <textarea className={inputClass} rows={2} value={row.action} onChange={(event) => updateStep(form, index, { action: event.target.value }, onChange)} onBlur={(event) => updateStep(form, index, { action: event.target.value }, onSave)} />
                    </td>
                    <td className="px-2 py-1 align-top">
                      <textarea className={inputClass} rows={2} value={row.result} onChange={(event) => updateStep(form, index, { result: event.target.value }, onChange)} onBlur={(event) => updateStep(form, index, { result: event.target.value }, onSave)} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Button
            type="button"
            variant="secondary"
            onClick={() => {
              const next = { ...form, troubleshooting: [...form.troubleshooting, { subsection: "", action: "", result: "" }] };
              onChange(next);
              onSave(next);
            }}
          >
            Add row
          </Button>
        </CardBody>
      </Card>
      <Card>
        <CardHeader>
          <CardTitleWithHelp hoverHint="Unedited observations from anyone on this RCA. These notes do not show up on the customer form.">
            Editorials and free-form notes
          </CardTitleWithHelp>
        </CardHeader>
        <CardBody className="space-y-3">
          {postedEditorials.length === 0 ? (
            <p className="text-sm text-slate-500">No notes yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-slate-500">
                    <th className="px-2 py-1 font-medium">Name</th>
                    <th className="px-2 py-1 font-medium">Date</th>
                    <th className="px-2 py-1 font-medium">Notes / editorial</th>
                  </tr>
                </thead>
                <tbody>
                  {postedEditorials.map((row, index) => (
                    <tr key={`note-${index}`} className="border-t border-slate-100 align-top">
                      <td className="px-2 py-2">{row.name || EM_DASH}</td>
                      <td className="px-2 py-2 whitespace-nowrap">{row.date || EM_DASH}</td>
                      <td className="px-2 py-2 whitespace-pre-wrap">{row.notes}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <label className="block space-y-1 text-sm">
            <span className="font-medium text-slate-700">Notes / editorial</span>
            <textarea
              className={inputClass}
              rows={8}
              value={editorialDraft}
              onChange={(event) => setEditorialDraft(event.target.value)}
            />
          </label>
          <Button
            type="button"
            disabled={editorialDraft.trim() === "" || authorName === ""}
            onClick={() => {
              const next = {
                ...form,
                editorials: [
                  { name: authorName, date: new Date().toLocaleDateString("en-CA"), notes: editorialDraft.trim() },
                  ...postedEditorials,
                ],
              };
              setEditorialDraft("");
              onChange(next);
              onSave(next);
            }}
          >
            Submit
          </Button>
        </CardBody>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>Working conclusion (internal, pre-customer)</CardTitle>
        </CardHeader>
        <CardBody className="space-y-3">
          <TextField label="Most likely root cause" multiline value={form.conclusion.rootCause} onChange={(value) => setConclusion("rootCause", value)} onBlur={(value) => saveConclusion("rootCause", value)} />
          <TextField label="Supporting evidence" multiline value={form.conclusion.evidence} onChange={(value) => setConclusion("evidence", value)} onBlur={(value) => saveConclusion("evidence", value)} />
          <TextField label="Open questions / unknowns" multiline value={form.conclusion.openQuestions} onChange={(value) => setConclusion("openQuestions", value)} onBlur={(value) => saveConclusion("openQuestions", value)} />
          <TextField label="Proposed corrective actions" multiline value={form.conclusion.correctiveActions} onChange={(value) => setConclusion("correctiveActions", value)} onBlur={(value) => saveConclusion("correctiveActions", value)} />
          {form.conclusion.signOff.name.trim() !== "" ? (
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <span className="font-medium text-slate-700">Engineering sign-off</span>
              <span className="inline-flex max-w-full items-center gap-1.5 rounded-full border border-slate-200 bg-white py-0.5 pl-0.5 pr-2.5 text-xs font-medium text-slate-800">
                <HubUserAvatar
                  avatarUrl={form.conclusion.signOff.avatarUrl || null}
                  label={form.conclusion.signOff.name}
                  size="sm"
                  className="h-5 w-5 text-[10px]"
                />
                <span className="truncate">{form.conclusion.signOff.name}</span>
              </span>
              <span className="text-slate-600">{form.conclusion.signOff.date}</span>
            </div>
          ) : (
            <Button type="button" disabled={authorName === ""} onClick={signOff}>
              Sign off
            </Button>
          )}
        </CardBody>
      </Card>
    </div>
  );
}

function ReportSheet({
  draft,
  report,
  onChange,
  onSave,
}: {
  draft: boolean;
  report: RcaReport;
  onChange: (next: RcaReport) => void;
  onSave: (next: RcaReport) => void;
}) {
  const set = (key: keyof RcaReport, value: string) => onChange({ ...report, [key]: value });
  const save = (key: keyof RcaReport, value: string) => onSave({ ...report, [key]: value });
  return (
    <Card>
      <CardHeader>
        <CardTitle>{draft ? "Root cause analysis report (draft)" : "Root cause analysis report"}</CardTitle>
      </CardHeader>
      <CardBody className="space-y-3">
        {draft ? (
          <p className="text-sm font-medium text-amber-800">Draft. This copy is not the customer final.</p>
        ) : null}
        <div className="grid gap-3 md:grid-cols-2">
          <TextField label="Report date" value={report.reportDate} onChange={(value) => set("reportDate", value)} onBlur={(value) => save("reportDate", value)} />
          <TextField label="Date received" value={report.dateReceived} onChange={(value) => set("dateReceived", value)} onBlur={(value) => save("dateReceived", value)} />
          <TextField label="Customer" value={report.customer} onChange={(value) => set("customer", value)} onBlur={(value) => save("customer", value)} />
          <TextField label="Rep" value={report.rep} onChange={(value) => set("rep", value)} onBlur={(value) => save("rep", value)} />
          <TextField label="Customer contact" value={report.customerContact} onChange={(value) => set("customerContact", value)} onBlur={(value) => save("customerContact", value)} />
          <TextField label="Phone number" value={report.phone} onChange={(value) => set("phone", value)} onBlur={(value) => save("phone", value)} />
          <TextField label="Model number" value={report.model} onChange={(value) => set("model", value)} onBlur={(value) => save("model", value)} />
        </div>
        <TextField label="Event description" multiline value={report.eventDescription} onChange={(value) => set("eventDescription", value)} onBlur={(value) => save("eventDescription", value)} />
        <TextField label="Analysis" multiline value={report.analysis} onChange={(value) => set("analysis", value)} onBlur={(value) => save("analysis", value)} />
        <TextField label="Corrective actions" multiline value={report.correctiveActions} onChange={(value) => set("correctiveActions", value)} onBlur={(value) => save("correctiveActions", value)} />
        <div className="grid gap-3 md:grid-cols-2">
          <TextField label="Signed by" value={report.signedBy} onChange={(value) => set("signedBy", value)} onBlur={(value) => save("signedBy", value)} />
          <TextField label="Date" value={report.signedDate} onChange={(value) => set("signedDate", value)} onBlur={(value) => save("signedDate", value)} />
        </div>
      </CardBody>
    </Card>
  );
}

function TextField({
  label,
  value,
  placeholder,
  multiline,
  onChange,
  onBlur,
}: {
  label: string;
  value: string;
  placeholder?: string;
  multiline?: boolean;
  onChange: (value: string) => void;
  onBlur: (value: string) => void;
}) {
  return (
    <label className="block space-y-1 text-sm">
      <span className="font-medium text-slate-700">{label}</span>
      {multiline ? (
        <textarea
          className={inputClass}
          rows={4}
          placeholder={placeholder}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          onBlur={(event) => onBlur(event.target.value)}
        />
      ) : (
        <input
          className={inputClass}
          placeholder={placeholder}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          onBlur={(event) => onBlur(event.target.value)}
        />
      )}
    </label>
  );
}

function formatAchievedAt(value: string): string {
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" });
}

function RcaStatusFlow({
  form,
  onChange,
  onSave,
}: {
  form: RcaFormState;
  onChange: (next: RcaFormState) => void;
  onSave: (next: RcaFormState) => void;
}) {
  const achievedIndex = RCA_FLOW_STEPS.reduce((latest, step, index) => (form.statusAchievedAt[step] ? index : latest), -1);
  const markStep = (step: RcaFlowStep) => {
    if (form.statusAchievedAt[step]) return;
    const next = {
      ...form,
      statusAchievedAt: { ...form.statusAchievedAt, [step]: new Date().toISOString() },
    };
    onChange(next);
    onSave(next);
  };
  const flagDisposition = () => {
    if (form.returnedForDispositionAt.trim() !== "") return;
    const next = { ...form, returnedForDispositionAt: new Date().toISOString() };
    onChange(next);
    onSave(next);
  };

  return (
    <div className="space-y-3 rounded-lg border border-slate-200 bg-white px-5 py-4 shadow-sm">
      <ol className="flex flex-wrap items-center gap-y-2">
        {RCA_FLOW_STEPS.map((step, index) => {
          const achievedAt = form.statusAchievedAt[step] ?? "";
          const achieved = achievedAt !== "";
          const current = index === achievedIndex;
          return (
            <li key={step} className="flex items-center">
              {index > 0 ? (
                <span className={cn("mx-1 h-px w-5", index <= achievedIndex ? "bg-sky-400" : "bg-slate-300")} aria-hidden />
              ) : null}
              <HoverHintTrigger
                trigger={
                  <button
                    type="button"
                    className={cn(
                      "rounded-full border px-3 py-1 text-left text-xs font-medium",
                      achieved
                        ? "border-sky-700 bg-sky-700 text-white"
                        : "border-slate-300 bg-white text-slate-600 hover:bg-slate-50",
                      current ? "ring-2 ring-sky-300 ring-offset-1" : "",
                    )}
                    onClick={() => markStep(step)}
                  >
                    {step}
                  </button>
                }
              >
                {achieved ? `Achieved ${formatAchievedAt(achievedAt)}` : "Not achieved yet. Select to record it now."}
              </HoverHintTrigger>
            </li>
          );
        })}
      </ol>
      <div className="border-t border-slate-100 pt-3">
        <HoverHintTrigger
          trigger={
            <button
              type="button"
              className={cn(
                "rounded-full border px-3 py-1 text-xs font-medium",
                form.returnedForDispositionAt.trim() !== ""
                  ? "border-amber-700 bg-amber-100 text-amber-950"
                  : "border-slate-300 bg-white text-slate-600 hover:bg-slate-50",
              )}
              onClick={flagDisposition}
            >
              {RCA_DISPOSITION_FLAG}
            </button>
          }
        >
          {form.returnedForDispositionAt.trim() !== ""
            ? `Flagged ${formatAchievedAt(form.returnedForDispositionAt)}`
            : "Not flagged. Select to record it now."}
        </HoverHintTrigger>
      </div>
    </div>
  );
}

function updateStep(
  form: RcaFormState,
  index: number,
  patch: Partial<RcaStepRow>,
  apply: (next: RcaFormState) => void,
) {
  const troubleshooting = form.troubleshooting.map((row, rowIndex) =>
    rowIndex === index ? { ...row, ...patch } : row,
  );
  apply({ ...form, troubleshooting });
}
